import { LABELS } from "../../../constants/labels/jetlift/jetlift.labels";
import TextShortInput from "../../common/TextShortInput";
import useJetliftBasicCalculationsFacade from "../../../facade/jetlift/jetlift-output/basicCalculation.facade";
import { useAppSelector } from "../../../store/hooks/StoreHooks";
import { useRef, useState } from "react";
import ButtonGroup from "../../common/button-group";
import SwitchButton from "../../common/switch-button";
import html2canvas from "html2canvas";
import { Controller } from "react-hook-form";
import TextInput from "../../common/Jetlift/TextInput";

const useOutputValidations = () => {
    const { depletionPressure, futureProductionRate, basicTDHCalculation, depletionPercentage, showIPRPlotInMainReport, showDepletionPlotInMainReport } = useAppSelector((state) => state?.jetlift?.basicCalculations);
    const {
        updateBasicIPRDepletionPressure,
        updateBasicIPRFutureProdRate,
        updateBasicDesrdPIP,
        updateShowIPRPlotInMainReport,
        updateShowDepletionPlotInMainReport,
    } = useJetliftBasicCalculationsFacade();
    const [basicTab, setBasicTab] = useState({
        shutInPressure: depletionPressure || "0",
        productionRate: futureProductionRate || "0",
        desiredPIP: basicTDHCalculation?.desrdPIP || "0",
    });
    const iprChartRef = useRef<HTMLDivElement | null>(null);
    const depletionChartRef = useRef<HTMLDivElement | null>(null);

    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const regex = /^[0-9]+(\.[0-9]*)?$/;
        const isValid = regex.test(value);
        if (isValid || value === "") {
            if (name === "shutinPressure") {
                setBasicTab((prev) => ({ ...prev, shutInPressure: +value }));
            } else if (name === "productionRate") {
                setBasicTab((prev) => ({ ...prev, productionRate: +value }));
            } else if (name === "desiredPIP") {
                setBasicTab((prev) => ({ ...prev, desiredPIP: value }));
            }
        }
    };

    const handleOnBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name } = e.target;
        if (name === "shutinPressure") {
            updateBasicIPRDepletionPressure(Number(basicTab?.shutInPressure));
        } else if (name === "productionRate") {
            updateBasicIPRFutureProdRate(Number(basicTab?.productionRate));
        } else if (name === "desiredPIP") {
            updateBasicDesrdPIP(Number(basicTab?.desiredPIP));
        }
        return;
    };

    const handleExport = (label: string) => {
        if (iprChartRef.current && label === "ipr") {
            html2canvas(iprChartRef.current, {
                backgroundColor: "#253040",
            }).then((canvas) => {
                const imgUrl = canvas.toDataURL("image/png");

                const link = document.createElement("a");
                link.href = imgUrl;
                link.download = "IPR-Plots.png";
                link.click();
            });
        };
        if (depletionChartRef.current && label === "depletion") {
            html2canvas(depletionChartRef.current, {
                backgroundColor: "#384252",

            }).then((canvas) => {
                const imgUrl = canvas.toDataURL("image/png");

                const link = document.createElement("a");
                link.href = imgUrl;
                link.download = "Depletion-Plot.png";
                link.click();
            });
        };
        return null;
    };

    const sectionsData = {
        basic: [
            {
                section: "Basic",
                fields: [
                    {
                        label: `${LABELS?.futureShutInPresssure}`,
                        type: "shortInput",
                        name: "shutinPressure",
                        unit: `${LABELS?.psi}`,
                        value: basicTab?.shutInPressure,
                        onChange: (e: any) => { handleOnChange(e) },
                        onBlur: (e: any) => { handleOnBlur(e) },
                    },
                    {
                        label: `${LABELS?.futureProductionRate}`,
                        type: "shortInput",
                        name: "productionRate",
                        unit: `${LABELS?.bpd}`,
                        value: basicTab?.productionRate,
                        onChange: (e: any) => { handleOnChange(e) },
                        onBlur: (e: any) => { handleOnBlur(e) },
                    },
                    {
                        label: `${LABELS?.depletion}`,
                        type: "text",
                        unit: `${LABELS?.percentage}`,
                        value: depletionPercentage || 0,
                    },
                ]
            },
            {
                section: "ipr-export",
                fields: [
                    {
                        label: `${LABELS?.wellInflowPerformance}`,
                        type: "text",
                    },
                    {
                        label: `${LABELS?.includeInMainReport}`,
                        type: "toggleSwitch",
                        checked: showIPRPlotInMainReport,
                        onChange: (checked: boolean) => updateShowIPRPlotInMainReport(checked),
                    },
                    {
                        label: `${LABELS?.exportIprPlot}`,
                        type: "button",
                        options: [{ id: "0", label: `${LABELS?.exportIprPlot}`, type: "secondary", icon: "" }],
                        onChange: () => { handleExport("ipr") },
                    }
                ]
            },
            {
                section: "depletion-export",
                fields: [
                    {
                        label: `${LABELS?.depletionCurve}`,
                        type: "text",
                    },
                    {
                        label: `${LABELS?.exportDepletionPlot}`,
                        type: "button",
                        options: [{ id: "0", label: `${LABELS?.exportDepletionPlot}`, type: "secondary", icon: "" }],
                        onChange: () => { handleExport("depletion") },
                    }
                ]
            }
        ],
        wellSurvey: [
            {
                section: "",
                fields: [
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.startCellMd}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.startCellInc}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.startCellAziDls}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.endCellMd}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.endCellInc}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.endCellAziDls}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: "",
                        type: `${LABELS?.inputType}`,
                        name: `${LABELS?.sheetNameMd}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    },
                    {
                        label: `${LABELS?.chooseFile}`,
                        type: `${LABELS?.fileType}`,
                        name: `${LABELS?.chooseFile}`,
                        value: "",
                        onChange: () => { },
                        rules: {
                            required: "",
                        },
                    }
                ]
            }
        ]
    };
    const renderFields = (
        section: any
    ) => {
        if (section?.section === "Basic") {
            return (
                <div className="flex justify-between items-center p-[16px]">
                    <div className="">
                        {
                            section?.fields?.map((field: any) => {
                                if (field?.type === "shortInput") {
                                    return (
                                        <div className={`flex items-center ${field?.label === LABELS?.futureShutInPresssure ? "mb-[16px]" : ""}`}>
                                            <label className="text-neutral-25 font-medium mr-2">
                                                {field?.label}
                                            </label>
                                            <div className="flex items-center">
                                                <TextShortInput
                                                    type="text"
                                                    value={field?.value}
                                                    onChange={(e) => field?.onChange(e)}
                                                    onBlur={(e: any) => field?.onBlur(e)}
                                                    name={field?.name}
                                                    className="w-[80px] px-[8px] py-[4px] border-[1px] rounded-[8px] border-darkcolor-400 bg-transparent focus:outline-none text-neutral-25 hover:border-primary-500 focus:ring-primary-500 focus-within:ring-1 focus-within:border-primary-500"
                                                />
                                                <span className="ml-2 text-neutral-25">{field?.unit}</span>
                                            </div>
                                        </div>
                                    )
                                }
                                return null;
                            })
                        }
                    </div>
                    {section?.fields?.map((field: any) => {
                        if (field?.type === "text") {
                            return (
                                <div>
                                    <p className="text-neutral-25 font-medium">
                                        {field?.label} {field?.unit} : <span className="font-bold">{field?.value}</span>
                                    </p>
                                </div>
                            )
                        }
                        return null;
                    })}
                </div>
            )
        }
        return null;
    };

    const renderExportFields = (section: any, label: string) => {
        if (section?.section === label && section?.section !== "Basic") {
            return (
                <div className="flex justify-between items-center">
                    {
                        section?.fields?.map((field: any) => {
                            return (
                                <>
                                    <div>{field?.type === "text" && (
                                        <label className="text-[18px] font-semibold text-neutral-25">
                                            {field?.label}
                                        </label>
                                    )}</div>


                                </>
                            )
                        })}
                    <div className="flex flex-end justify-end">
                        {section?.fields?.map((field: any) => (
                            <>
                                {field?.type === "toggleSwitch" && (
                                    <SwitchButton label={`${LABELS?.includeInMainReport}`} onChange={field?.onChange} checked={field?.checked} />)}
                                {field?.type === "button" && (
                                    <ButtonGroup buttonOptions={field?.options} handleClick={field?.onChange} />)}
                            </>
                        ))}
                    </div>
                </div>
            )
        };
        return null;
    };

    const renderWellSurveyFields = (field: any, control: any) => {
        return (
            <tr className='border-b-[1px] border-darkcolor-400'>
                {(field?.type === "input" || field?.type === "number") && (
                <Controller
                    name={field?.label}
                    control={control || {}}
                    rules={field?.rules}
                    render={({ field: formField, fieldState: { error } }) => (
                        <TextInput
                            name={field?.label}
                            label={field?.label}
                            type={field?.type === 'number' ? 'number' : 'text'}
                            placeholder={field?.placeholder || `Enter ${field?.label}`}
                            unit={field?.unit}
                            error={error?.message}
                            control={control}
                            rules={field?.rules}
                            onChange={field?.onChange}
                            onBlur={field?.onBlur}
                            value={field?.value}
                        />
                    )}
                />
                )}
            </tr>
        )

    };

    return {
        sectionsData,
        renderFields,
        renderExportFields,
        iprChartRef,
        depletionChartRef,
    };
};

export default useOutputValidations;